/**
 * The Studio Happens mark.
 *
 * One component rather than four copies, because there were four copies of the
 * placeholder it replaces and they had already drifted — two at 32px, one at
 * 40px, each with its own radius.
 *
 * It sits on a light tile everywhere, and that is not decoration. The mark is
 * black and magenta on a transparent ground: on the shell, which is
 * `#101014`, the black half of it simply disappears and what is left reads as
 * a broken image rather than a logo. The tile is the smallest thing that makes
 * one asset work on both the dark sign-in gradient and the cream loader, and
 * it matches what the rest of the interface already does — white cards
 * floating on a dark canvas.
 *
 * `alt` is empty on purpose. Every place this is drawn, the words "Hapi OS"
 * are next to it in real text, so a screen reader announcing the logo as well
 * would say the name twice.
 */

import { cn } from '../lib/cn';

interface BrandMarkProps {
  /** The tile, in pixels. The mark is inset within it. */
  size?: number;
  className?: string;
}

export function BrandMark({ size = 32, className }: BrandMarkProps) {
  // Tailwind cannot see a class name it never reads, so the sizes are inline
  // rather than `h-${size}`.
  const inset = Math.round(size * 0.78);

  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center justify-center overflow-hidden bg-white',
        'ring-1 ring-black/5',
        size >= 40 ? 'rounded-xl' : 'rounded-lg',
        className
      )}
      style={{ width: size, height: size }}
    >
      <img
        src="/brand-mark.png"
        alt=""
        width={inset}
        height={inset}
        style={{ width: inset, height: inset }}
        className="object-contain"
      />
    </span>
  );
}
